// src/pages/ResetPasswordPage.tsx — Pantalla de reseteo de contraseña para Administradores POW
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react'
import { api } from '@/services/api'
import logoLight from '@/assets/logo-light.png'
import clsx from 'clsx'
import { validatePasswordStrength } from '@/utils/passwordValidation'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!token) {
      setError('Token de recuperación no válido o ausente.')
      return
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.')
      return
    }

    const check = validatePasswordStrength(password)
    if (!check.ok) {
      setError(check.msg || 'Contraseña débil.')
      return
    }

    setLoading(true)
    try {
      await api.post('/api/auth/contrasena/confirmar', {
        token,
        nueva_contrasena: password,
      })
      setSuccess('Tu contraseña de administrador ha sido restablecida con éxito.')
      setPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      const detail = err.response?.data?.detail
      let friendlyError = 'Ocurrió un error al restablecer la contraseña.'
      if (detail === 'token_expirado') {
        friendlyError = 'El enlace de recuperación ha expirado. Por favor, solicita uno nuevo.'
      } else if (detail === 'token_ya_usado') {
        friendlyError = 'Este enlace de recuperación ya ha sido utilizado.'
      } else if (detail === 'token_invalido') {
        friendlyError = 'El enlace de recuperación es inválido.'
      } else if (detail === 'password_sin_mayuscula') {
        friendlyError = 'La contraseña necesita al menos una letra mayúscula.'
      } else if (detail === 'password_sin_numero') {
        friendlyError = 'La contraseña necesita al menos un número.'
      } else if (detail === 'password_sin_especial') {
        friendlyError = 'La contraseña necesita al menos un carácter especial.'
      } else if (detail === 'password_debil') {
        friendlyError = 'La contraseña no cumple con los requisitos de seguridad.'
      }
      setError(friendlyError)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: '#FFFFFF' }}>

      {/* Orbs decorativos */}
      <div className="absolute rounded-full animate-pulse"
        style={{
          width: '500px', height: '500px',
          background: '#0050F0', opacity: 0.12,
          top: '-150px', left: '-100px',
          filter: 'blur(100px)'
        }} />
      <div className="absolute rounded-full animate-pulse"
        style={{
          width: '400px', height: '400px',
          background: '#00DCA0', opacity: 0.10,
          bottom: '-100px', right: '-80px',
          filter: 'blur(100px)',
          animationDelay: '1s'
        }} />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[420px] mx-4 fade-in">
        <div className="card" style={{ padding: '40px', background: '#FFFFFF', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.05)', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>

          {/* Logo */}
          <div className="flex flex-col items-center mb-[28px]">
            <img src={logoLight} alt="MIVA" className="h-[200px] mb-[10px] object-contain" />
            <h1 className="text-[1.5rem] font-[800] text-center mb-[4px]" style={{ color: '#071B3F' }}>
              Nueva contraseña
            </h1>
            <p className="text-center text-[0.85rem]" style={{ color: '#5E6E89' }}>
              Panel Administrador — <span className="inline-flex items-center rounded-full font-[600] tracking-[0.02em]" style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6', padding: '3px 10px', fontSize: '0.72rem' }}>SaaS</span>
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm"
              style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
              {error}
            </div>
          )}

          {/* Éxito */}
          {success ? (
            <div className="text-center space-y-[16px] py-[10px]">
              <div className="flex justify-center">
                <div className="flex items-center justify-center rounded-full"
                  style={{ width: 56, height: 56, background: 'rgba(16,185,129,0.10)', border: '1.5px solid rgba(16,185,129,0.25)' }}>
                  <span style={{ fontSize: 26 }}>✅</span>
                </div>
              </div>
              <p className="text-[0.95rem] font-[700]" style={{ color: '#064E3B' }}>
                ¡Contraseña restablecida!
              </p>
              <p className="text-[0.85rem] leading-[1.6]" style={{ color: '#5E6E89' }}>
                {success} Ya puedes iniciar sesión con tus nuevas credenciales.
              </p>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="btn btn-primary btn-block mt-[12px]"
              >
                Ir al inicio de sesión
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-[14px]">
              {/* Nueva contraseña */}
              <div className="flex flex-col gap-[6px]">
                <label className="text-[0.8rem] font-[600] tracking-[0.01em]" style={{ color: '#5E6E89' }}>
                  Nueva contraseña
                </label>
                <div className="relative flex items-center">
                  <Lock size={15} className="absolute left-[14px]" style={{ color: '#94A3B8', pointerEvents: 'none' }} />
                  <input
                    id="new-password-input"
                    className="w-full rounded-[12px] pl-[38px] pr-[40px] py-[10px] text-[0.9rem] outline-none transition-shadow"
                    style={{ background: '#F5F7FA', border: '1.5px solid #CBD5E1', color: '#071B3F' }}
                    type={showPass ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0,80,240,0.15)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-[12px] text-[#94A3B8] hover:text-[#071B3F] bg-transparent border-none cursor-pointer"
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirmar contraseña */}
              <div className="flex flex-col gap-[6px]">
                <label className="text-[0.8rem] font-[600] tracking-[0.01em]" style={{ color: '#5E6E89' }}>
                  Confirmar contraseña
                </label>
                <div className="relative flex items-center">
                  <Lock size={15} className="absolute left-[14px]" style={{ color: '#94A3B8', pointerEvents: 'none' }} />
                  <input
                    id="confirm-password-input"
                    className="w-full rounded-[12px] pl-[38px] pr-[40px] py-[10px] text-[0.9rem] outline-none transition-shadow"
                    style={{ background: '#F5F7FA', border: '1.5px solid #CBD5E1', color: '#071B3F' }}
                    type={showConfirmPass ? 'text' : 'password'}
                    placeholder="Repite tu contraseña"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0,80,240,0.15)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    className="absolute right-[12px] text-[#94A3B8] hover:text-[#071B3F] bg-transparent border-none cursor-pointer"
                  >
                    {showConfirmPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="pt-[4px]">
                <button
                  id="reset-submit-btn"
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className={clsx(
                    'btn btn-primary btn-block',
                    (loading || !password || !confirmPassword) && 'opacity-60 cursor-not-allowed'
                  )}
                >
                  {loading ? 'Guardando contraseña...' : 'Restablecer contraseña'}
                </button>
              </div>
            </form>
          )}

          {/* Volver al login */}
          <div className="text-center mt-[20px] pt-[16px]" style={{ borderTop: '1px solid #F1F5F9' }}>
            <button
              type="button"
              id="back-to-login-btn"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-[6px] text-[0.82rem] font-[500] transition-colors hover:underline bg-transparent border-none cursor-pointer"
              style={{ color: '#5E6E89' }}
            >
              <ArrowLeft size={14} /> Volver al inicio de sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
